/**
 * Cookie consent management utility.
 *
 * Consent categories:
 * - essential: Always on (auth cookies, CSRF). Cannot be toggled off.
 * - analytics: Sentry error tracking + replay. Requires opt-in for GDPR.
 *
 * Consent is stored as:
 * - A JSON cookie `aq_consent` (readable server-side and in instrumentation)
 * - Mirrored to localStorage for faster client reads
 */

export interface ConsentState {
  essential: true; // always true
  analytics: boolean;
}

const CONSENT_COOKIE = 'aq_consent';
const CONSENT_STORAGE_KEY = 'aq_consent';
const CONSENT_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

/**
 * Read consent state from cookie (works in instrumentation-client.ts and components).
 * Returns null if no consent decision has been made yet.
 */
export function getConsentFromCookie(): ConsentState | null {
  if (typeof document === 'undefined') return null;

  const match = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${CONSENT_COOKIE}=`));

  if (!match) return null;

  try {
    return JSON.parse(decodeURIComponent(match.split('=')[1])) as ConsentState;
  } catch {
    return null;
  }
}

/**
 * Read consent state from localStorage (faster, client-only fallback).
 */
export function getConsentFromStorage(): ConsentState | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConsentState) : null;
  } catch {
    return null;
  }
}

/**
 * Get the current consent state from any available source.
 */
export function getConsent(): ConsentState | null {
  return getConsentFromCookie() ?? getConsentFromStorage();
}

/**
 * Check if analytics consent has been granted.
 */
export function hasAnalyticsConsent(): boolean {
  const consent = getConsent();
  return consent?.analytics === true;
}

/**
 * Persist consent state to both cookie and localStorage.
 */
export function setConsent(state: ConsentState): void {
  const json = JSON.stringify(state);

  // Set cookie — SameSite=Lax, Secure in production
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(json)}; path=/; max-age=${CONSENT_MAX_AGE}; SameSite=Lax${secure}`;

  // Mirror to localStorage
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, json);
  } catch {
    // localStorage may be unavailable in private browsing
  }
}
